import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { activityApi } from "@/lib/api";
import { History, Plus, Pencil, Trash2, ArrowRight } from "lucide-react";

const fmt = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

const ACTION = {
  create: { label: "angelegt", icon: Plus, cls: "bg-success/10 text-success" },
  update: { label: "geändert", icon: Pencil, cls: "bg-info/10 text-info" },
  delete: { label: "gelöscht", icon: Trash2, cls: "bg-destructive/10 text-destructive" },
};

const val = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "Ja";
  if (v === false) return "Nein";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
};

export const CustomerAuditTab = ({ entityType = "customer", entityId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await activityApi.getAudit(entityType, entityId);
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.debug("Verlauf laden:", e); }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="audit-tab-content">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />Änderungsverlauf</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Wird geladen…</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4 text-center">Noch keine Änderungen protokolliert.</p>
        ) : (
          <ol className="relative border-l border-border ml-2 space-y-5" data-testid="audit-list">
            {logs.map((log) => {
              const a = ACTION[log.action] || ACTION.update;
              const Icon = a.icon;
              return (
                <li key={log.id} className="ml-5" data-testid={`audit-${log.id}`}>
                  <span className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full ${a.cls}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex flex-wrap items-center gap-x-2 text-sm">
                    <span className="font-medium">{log.actor_name || "System"}</span>
                    <span className="text-muted-foreground">hat {a.label}</span>
                    <span className="text-xs text-muted-foreground">· {fmt(log.created_at)}</span>
                  </div>
                  {log.summary && <p className="text-sm text-muted-foreground mt-1">{log.summary}</p>}
                  {log.changes && log.changes.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {log.changes.map((c, i) => (
                        <li key={i} className="text-xs bg-muted/50 rounded px-2 py-1.5">
                          <span className="font-medium">{c.label}: </span>
                          {c.long_text ? (
                            <span className="text-muted-foreground italic">{c.note || "geändert"}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 flex-wrap">
                              <span className="line-through text-muted-foreground">{val(c.old)}</span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span className="text-foreground font-medium">{val(c.new)}</span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
